import { Button, Card, Link, Stack, Switch, Typography } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { C_SURFACE } from "~/common/components/icon/Colors";
import { useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogHeader, DialogTitle } from "~/common/mui/Dialog/Dialog";
import { useDispatch, useSelector } from "react-redux";
import { selectIsUseNewDesign } from "~/domains/userPreferences/redux/selectors";
import { changeNewDesignThunk } from "~/domains/userPreferences/redux/userPreferencesSlice";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";

export interface NewDesignToggleProps {
  checked: boolean;
  onCheck: (checked: boolean) => void;
  className?: string;
}

const IFRAME_URL = "https://schaefflertech.qualtrics.com/jfe/form/SV_esa1k0NANhT4wPY";

export const ConnectedNewDesignToggle = ({ className }: Omit<NewDesignToggleProps, "onCheck" | "checked">) => {
  const useNewDesign = useSelector(selectIsUseNewDesign);
  const dispatch = useDispatch();

  return (
    <NewDesignToggle
      checked={useNewDesign}
      onCheck={(checked) => dispatch(changeNewDesignThunk(checked))}
      className={className}
    />
  );
};

/**
 * NewDesignToggle
 * Visual card prompting users to try the new product results design with a toggle and feedback link.
 * Figma: Selection assistant card with body text, link-styled feedback, and a switch with "New" label.
 */
export const NewDesignToggle = ({ checked, onCheck, className }: NewDesignToggleProps) => {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(false);
  const closeModal = () => setShowModal(false);
  const isDesktop = useIsMinDesktopSize();

  return (
    <>
      <Card className={className} elevation={1} sx={{ backgroundColor: C_SURFACE, px: 2, py: 1 }} variant="elevation">
        <Stack
          direction={{ phone: "column", tablet: "row" }}
          alignItems={{ phone: "flex-start", tablet: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction={{ phone: "column", tablet: "row" }} gap={0.5}>
            <Typography color="text.primary">{t("feature.search.newDesign.description")}</Typography>
            <Link component={"button"} onClick={() => setShowModal(true)}>
              {t("feature.search.newDesign.feedback")}
            </Link>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1}>
            <Switch checked={checked} onChange={(e, value) => onCheck(value)} />
            <Typography color={checked ? "primary" : "text.primary"}>
              {t(`feature.search.newDesign.toggle.${checked ? "new" : "old"}`)}
            </Typography>
          </Stack>
        </Stack>
      </Card>
      <Dialog open={showModal} onClose={closeModal} maxWidth="desktop-s">
        <DialogHeader onClose={closeModal}>
          <DialogTitle sx={{ p: 0 }}>Feedback</DialogTitle>
        </DialogHeader>
        <DialogContent
          sx={{
            padding: isDesktop ? "24px 24px 0 24px" : "24px 16px 0 16px",
          }}
        >
          <iframe style={{ width: "100%", height: "600px", border: "none" }} src={IFRAME_URL} />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewDesignToggle;
